import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  makeStyles,
  Backdrop,
  CircularProgress,
  InputAdornment,
  Grid,
  Typography,
} from "@material-ui/core";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import SaveIcon from "@material-ui/icons/Save";

import getIP from "axios";
import api from "../../../axios";
import Api from "../../../Api";
import { useAlert } from "react-alert";
import { Country, State, City } from "country-state-city";
import Autocomplete from "@material-ui/lab/Autocomplete";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  card: {
    borderRadius: 12,
    overflow: "hidden",
  },

  cardHeader: {
    backgroundColor: "#fafafa",
    padding: theme.spacing(2.5, 3),
  },

  headerTitle: {
    fontWeight: 600,
  },

  section: {
    marginBottom: theme.spacing(3),
  },

  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },

  sectionDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2.5),
  },

  field: {
    width: "100%",
  },

  actionBar: {
    padding: theme.spacing(2),
    backgroundColor: "#fafafa",
    display: "flex",
    justifyContent: "flex-end",
  },

  saveButton: {
    minWidth: 150,
    height: 44,
    fontWeight: 600,
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const UserCreate = ({ className, ...rest }) => {
  const classes = useStyles();
  const alert = useAlert();

  // =========================
  // STATES
  // =========================
  const [loading, setLoading] = useState(false);
  const [ip_address, setIpAddress] = useState("");
  const [group, setGroup] = useState([]);
  const [type, setType] = useState("");

  const [getIsocode, setIsocOde] = useState("");
  const [getCountry, setCountry] = useState("");
  const [getCityCode, setCityCode] = useState("");
  const [getCity, setGetCity] = useState("");
  const [getState, setGetState] = useState("");

  const [values, setValues] = useState({
    firstname: "",
    lastname: "",
    dob: "",
    address: "",
    postal_code: "",
    email: "",
    password: "",
    confirm: "",
    phone: "",
    nationality: "",
  });

  const [errorMaximun, setErrorMaximum] = useState("");
  const [errorMax, setErrorMax] = useState(false);
  const [valid, setValid] = useState("");
  const [error, setError] = useState(false);

  // =========================
  // FETCH IP + GROUPS
  // =========================
  useEffect(() => {
    setLoading(true);

    getIP
      .get(`https://api64.ipify.org/?format=json`)
      .then((res) => {
        setIpAddress(res.data.ip);
      });

    api
      .get(`${Api.request.URL}/users/get/groups`)
      .then((res) => {
        setGroup(res.data.data || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Group Fetch Error:", error);
        setLoading(false);
      });
  }, []);

  // =========================
  // HANDLE INPUT
  // =========================
  const handleChange = (event) => {
    setValues({
      ...values,
      [event.target.name]: event.target.value,
    });

    if (event.target.name === "phone") {
      if (event.target.value.length === 10) {
        setErrorMax(false);
        setErrorMaximum("");
      } else {
        setErrorMax(true);
        setErrorMaximum("Sample Format Number: 9123456789");
      }
    }
  };

  // =========================
  // GROUP
  // =========================
  const handlepickGroup = (e, v) => {
    if (v) {
      setType(v.userType);
    } else {
      setType("");
    }
  };

  // =========================
  // COUNTRY
  // =========================
  const handlepickCountry = (e, v) => {
    if (v) {
      setIsocOde(v.value);
      setCountry(v.displayValue);
    } else {
      setIsocOde("");
      setCountry("");
    }
  };

  // =========================
  // STATE
  // =========================
  const handlepickState = (e, v) => {
    if (v) {
      setCityCode(v.value);
      setGetState(v.displayValue);
    } else {
      setCityCode("");
      setGetState("");
    }
  };

  // =========================
  // CITY
  // =========================
  const handlepickCity = (e, v) => {
    if (v) {
      setGetCity(v.value);
    } else {
      setGetCity("");
    }
  };

  // =========================
  // SUBMIT
  // =========================
  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (errorMax) {
      return alert.error("Wrong Phone Number Format");
    }

    if (values.password !== values.confirm) {
      setValid("Password and confirm password does not match");
      setError(true);
      return;
    }

    setLoading(true);

    const ArrayAddUser = {
      ...values,
      ip_address: ip_address,
      second_address_line: getState,
      city: getCity,
      country: getCountry,
      user_type: type,
    };

    api
      .post(`${Api.request.URL}/users`, ArrayAddUser)
      .then((res) => {
        alert.success(res.data.msg);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);

        const errorData = error.response?.data;

        if (errorData) {
          if (typeof errorData.errors === "string") {
            alert.error(errorData.errors);
          } else if (Array.isArray(errorData.errors)) {
            alert.error(errorData.errors.join(", "));
          } else if (errorData.msg) {
            alert.error(errorData.msg);
          } else {
            alert.error(
              "Something went wrong. Please try again."
            );
          }
        } else {
          alert.error("Server connection failed.");
        }
      });
  };

  // =========================
  // COUNTRY / STATE / CITY DATA
  // =========================
  const dataCountries = Country.getAllCountries().map((c) => ({
    value: c.isoCode,
    displayValue: c.name,
  }));

  const stateData = State.getStatesOfCountry(getIsocode).map((s) => ({
    value: s.isoCode,
    displayValue: s.name,
  }));

  const dataCity = City.getCitiesOfState(
    getIsocode,
    getCityCode
  ).map((city) => ({
    value: city.name,
    displayValue: city.name,
  }));

  return (
    <form
      onSubmit={handleFormSubmit}
      className={clsx(classes.root, className)}
      {...rest}
    >
      <Card
        className={classes.card}
        elevation={1}
      >
        {/* =========================
            HEADER
        ========================= */}
        <CardHeader
          className={classes.cardHeader}
          avatar={<PersonAddIcon color="primary" />}
          title={
            <Typography
              variant="h6"
              className={classes.headerTitle}
            >
              Create User
            </Typography>
          }
          subheader="Enter the user's account and contact information"
        />

        <Divider />

        <CardContent>
          {/* =========================
              PERSONAL INFORMATION
          ========================= */}
          <Box className={classes.section}>
            <Typography
              variant="h6"
              className={classes.sectionTitle}
            >
              Personal Information
            </Typography>

            <Typography
              variant="body2"
              className={classes.sectionDescription}
            >
              Enter the user's basic personal details.
            </Typography>

            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstname"
                  value={values.firstname}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastname"
                  value={values.lastname}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={values.dob}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Nationality"
                  name="nationality"
                  value={values.nationality}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* =========================
              CONTACT INFORMATION
          ========================= */}
          <Box className={classes.section} mt={3}>
            <Typography
              variant="h6"
              className={classes.sectionTitle}
            >
              Contact Information
            </Typography>

            <Typography
              variant="body2"
              className={classes.sectionDescription}
            >
              Add the user's phone number and email address.
            </Typography>

            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  error={errorMax}
                  helperText={errorMaximun}
                  label="Phone Number"
                  name="phone"
                  value={values.phone}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        +63
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* =========================
              LOCATION
          ========================= */}
          <Box className={classes.section} mt={3}>
            <Typography
              variant="h6"
              className={classes.sectionTitle}
            >
              Location
            </Typography>

            <Typography
              variant="body2"
              className={classes.sectionDescription}
            >
              Select the user's country, state, city, and address.
            </Typography>

            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <Autocomplete
                  options={dataCountries}
                  getOptionLabel={(option) =>
                    option.displayValue || ""
                  }
                  onChange={handlepickCountry}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Autocomplete
                  options={stateData}
                  getOptionLabel={(option) =>
                    option.displayValue || ""
                  }
                  onChange={handlepickState}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Autocomplete
                  options={dataCity}
                  getOptionLabel={(option) =>
                    option.displayValue || ""
                  }
                  onChange={handlepickCity}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={values.address}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  name="postal_code"
                  value={values.postal_code}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* =========================
              USER ACCESS
          ========================= */}
          <Box className={classes.section} mt={3}>
            <Typography
              variant="h6"
              className={classes.sectionTitle}
            >
              User Access
            </Typography>

            <Typography
              variant="body2"
              className={classes.sectionDescription}
            >
              Assign the appropriate user type and account password.
            </Typography>

            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <Autocomplete
                  id="user-type-select"
                  options={group}
                  getOptionLabel={(option) =>
                    option && option.userType
                      ? option.userType
                      : ""
                  }
                  getOptionSelected={(option, value) =>
                    option.userType === value.userType
                  }
                  onChange={handlepickGroup}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="User Type"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  error={error}
                  value={values.password}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirm"
                  type="password"
                  error={error}
                  helperText={valid}
                  value={values.confirm}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>
        </CardContent>

        <Divider />

        {/* =========================
            ACTION
        ========================= */}
        <Box className={classes.actionBar}>
          <Button
            style={Api.Button_save}
            variant="contained"
            color="primary"
            type="submit"
            startIcon={<SaveIcon />}
            className={classes.saveButton}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Details"}
          </Button>
        </Box>
      </Card>

      {/* =========================
          LOADING
      ========================= */}
      <Backdrop
        className={classes.backdrop}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </form>
  );
};

UserCreate.propTypes = {
  className: PropTypes.string,
};

export default UserCreate;